import React, { useState, useEffect } from 'react';
import { apiKeyService } from './apiKeyService';
import { APIKey, APIKeyCreateRequest } from './apiKey';
import { Badge, Box, Button, Container, Heading, Text, Spinner, Flex, HStack } from '@chakra-ui/react';
import ApiKeyForm from './ApiKeyForm';
import { ContentCard } from '../Components/ContentCard';

const ApiKeysManagement: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [newApiKey, setNewApiKey] = useState<APIKey | null>(null);
  const [formData, setFormData] = useState<APIKeyCreateRequest>({ name: '', projectId: '' });
  const [editingKeyId, setEditingKeyId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Load API keys on component mount
  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    try {
      setLoading(true);
      const keys = await apiKeyService.getAllApiKeys();
      setApiKeys(Array.isArray(keys) ? keys : []);
      setError(null);
    } catch (err) {
      setError('Failed to load API keys. Please try again.');
      console.error('Error loading API keys:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApiKeyCreated = (createdKey: APIKey) => {
    setApiKeys([...apiKeys, createdKey]);
    setNewApiKey(createdKey);
    setShowForm(false);
    setFormData({ name: '', projectId: '' });
    setSuccess('API key created successfully!');
    // Clear success message after 5 seconds
    setTimeout(() => setSuccess(null), 5000);
  };

  const handleApiKeyUpdated = (updatedKey: APIKey) => {
    setApiKeys(apiKeys.map(key => key.id === updatedKey.id ? updatedKey : key));
    setEditingKeyId(null);
    setFormData({ name: '', projectId: '' });
    setSuccess('API key updated successfully!');
    // Clear success message after 5 seconds
    setTimeout(() => setSuccess(null), 5000);
  };

  const handleDeleteApiKey = async () => {
    if (!deleteConfirmId) return;
    
    try {
      await apiKeyService.deleteApiKey(deleteConfirmId);
      setApiKeys(apiKeys.filter(key => key.id !== deleteConfirmId));
      setDeleteConfirmId(null);
      setSuccess('API key deleted successfully!');
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError('Failed to delete API key.');
      console.error('Error deleting API key:', err);
      // Clear error message after 5 seconds
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleToggleApiKeyStatus = async (id: string) => {
    try {
      const updatedKey = await apiKeyService.toggleApiKeyStatus(id);
      setApiKeys(apiKeys.map(key => key.id === id ? updatedKey : key));
      setSuccess(`API key ${updatedKey.isActive ? 'activated' : 'deactivated'} successfully!`);
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError('Failed to update API key status.');
      console.error('Error toggling API key status:', err);
      // Clear error message after 5 seconds
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleCloseNewKeyAlert = () => {
    setNewApiKey(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const startEditing = (apiKey: APIKey) => {
    setEditingKeyId(apiKey.id);
    setFormData({
      name: apiKey.name,
      projectId: apiKey.projectId || ''
    });
  };

  const cancelEditing = () => {
    setEditingKeyId(null);
    setFormData({ name: '', projectId: '' });
  };

  return (
    <Container maxW="6xl">
      <Box py={{ base: 6, md: 10 }}>
        <HStack justify="space-between" align="flex-start" mb={6} flexWrap="wrap" gap={3}>
          <Box>
            <Heading as="h1" size="lg" color="gray.900">API Keys</Heading>
            <Text color="gray.600" mt={1}>Create, rotate, and manage keys used to access your API.</Text>
          </Box>
          {!showForm && !editingKeyId && !deleteConfirmId && (
            <Button onClick={() => setShowForm(true)} colorScheme="blue">
              Create API Key
            </Button>
          )}
        </HStack>

        {newApiKey && (
          <Box mb={4} p={4} borderWidth="1px" borderColor="blue.100" borderRadius="xl" bg="blue.50" boxShadow="sm">
            <Heading as="h4" size="md" mb={2} color="gray.900">API Key Created</Heading>
            <Text mb={3} color="gray.700">
              Copy your new API key now. For security reasons, it won't be shown again.
            </Text>
            <Box p={3} bg="white" borderRadius="lg" borderWidth="1px" borderColor="gray.200" wordBreak="break-all" fontFamily="mono">
              {newApiKey.key}
            </Box>
            <Button size="sm" mt={3} onClick={handleCloseNewKeyAlert} variant="outline" colorScheme="blue">Close</Button>
          </Box>
        )}

        {showForm && (
          <Box mb={4}>
            <ApiKeyForm 
              onSuccess={handleApiKeyCreated} 
              onCancel={() => setShowForm(false)} 
            />
          </Box>
        )}

        {editingKeyId && (
          <Box mb={4}>
            <ApiKeyForm 
              apiKey={apiKeys.find(key => key.id === editingKeyId)} 
              onSuccess={handleApiKeyUpdated} 
              onCancel={cancelEditing} 
            />
          </Box>
        )}

        {deleteConfirmId && (
          <ContentCard>
            <Heading as="h3" size="md" mb={2} color="gray.900">Delete API Key</Heading>
            <Text mb={4} color="gray.700">Are you sure? This action cannot be undone.</Text>
            <Flex justifyContent="flex-end" gap={3}>
              <Button variant="outline" colorScheme="blue" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
              <Button colorScheme="red" onClick={handleDeleteApiKey}>Delete</Button>
            </Flex>
          </ContentCard>
        )}

        {error && (
          <ContentCard>
            <Heading as="h4" size="sm" color="red.700" mb={1}>Error</Heading>
            <Text color="red.700">{error}</Text>
            <Button mt={3} size="sm" variant="outline" onClick={() => setError(null)}>Dismiss</Button>
          </ContentCard>
        )}
        
        {success && (
          <Box mb={4} p={4} borderWidth="1px" borderColor="blue.100" borderRadius="xl" bg="blue.50" boxShadow="sm">
            <Heading as="h4" size="sm" color="green.700" mb={1}>Success</Heading>
            <Text color="green.700">{success}</Text>
            <Button mt={3} size="sm" variant="outline" colorScheme="blue" onClick={() => setSuccess(null)}>Dismiss</Button>
          </Box>
        )}

        {loading ? (
          <Flex justifyContent="center" p={6}>
            <Spinner size="lg" />
          </Flex>
        ) : apiKeys.length === 0 ? (
          <ContentCard>
            <Text color="gray.700">No API keys found. Create your first API key to get started.</Text>
          </ContentCard>
        ) : (
          <Box borderWidth="1px" borderColor="gray.200" borderRadius="xl" overflow="hidden" bg="white" boxShadow="sm">
            <Box as="table" width="100%" style={{ borderCollapse: 'collapse' }}>
              <Box as="thead" bg="gray.50">
                <Box as="tr">
                  <Box as="th" p={3} textAlign="left">Name</Box>
                  <Box as="th" p={3} textAlign="left">Project</Box>
                  <Box as="th" p={3} textAlign="left">Created</Box>
                  <Box as="th" p={3} textAlign="left">Tokens Used</Box>
                  <Box as="th" p={3} textAlign="left">Status</Box>
                  <Box as="th" p={3} textAlign="right">Actions</Box>
                </Box>
              </Box>
              <Box as="tbody">
                {apiKeys.map((apiKey) => (
                  <Box as="tr" key={apiKey.id} borderTopWidth="1px">
                    <Box as="td" p={3}>{apiKey.name}</Box>
                    <Box as="td" p={3}>{apiKey.projectId || <Text color="gray.500">No project</Text>}</Box>
                    <Box as="td" p={3}>{new Date(apiKey.createdAt).toLocaleDateString()}</Box>
                    <Box as="td" p={3}>{apiKey.tokensUsed ?? 0}</Box>
                    <Box as="td" p={3}>
                      <Badge colorScheme={apiKey.isActive ? 'green' : 'gray'} variant="subtle" px={3} py={1} borderRadius="full">
                        {apiKey.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </Box>
                    <Box as="td" p={3} textAlign="right">
                      <Button 
                        size="sm"
                        colorScheme={apiKey.isActive ? "red" : "green"}
                        mr={2}
                        onClick={() => handleToggleApiKeyStatus(apiKey.id)}
                      >
                        {apiKey.isActive ? "Deactivate" : "Activate"}
                      </Button>
                      <Button 
                        size="sm" 
                        mr={2}
                        onClick={() => startEditing(apiKey)}
                      >
                        Edit
                      </Button>
                      <Button 
                        size="sm" 
                        colorScheme="red"
                        onClick={() => setDeleteConfirmId(apiKey.id)}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default ApiKeysManagement;
